import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@blauturquesa/shared/locales';
import { countByMode, type PublicIndexCard } from '@blauturquesa/shared/public-view';
import { Screen } from '../../shell/Screen';
import { EmptyState } from '../../components/EmptyState';
import { Loading } from '../../components/Loading';
import { getPublicIndex } from './publicData';
import { Photo, pick, priceFromText } from './bits';

const MODES = ['shortRent', 'longRent', 'sale'] as const;
type Filter = 'all' | (typeof MODES)[number];

/** Explora (§10.4): totes les propietats publicades, amb filtre per tipus. Una sola lectura. */
export function ExplorePage() {
  const { t, i18n } = useTranslation(['public', 'shell']);
  const locale = i18n.language as Locale;
  const [cards, setCards] = useState<PublicIndexCard[] | null | undefined>(undefined);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    getPublicIndex().then((doc) => setCards(doc?.cards ?? []), () => setCards(null));
  }, []);

  if (cards === undefined) return <Screen title={t('shell:tabs.explore')}><Loading /></Screen>;
  if (cards === null) {
    return <Screen title={t('shell:tabs.explore')}><EmptyState icon="globe" title={t('offline.title')}>{t('offline.body')}</EmptyState></Screen>;
  }
  if (cards.length === 0) {
    return <Screen title={t('shell:tabs.explore')}><EmptyState icon="compass" title={t('shell:explore.emptyTitle')}>{t('shell:explore.emptyBody')}</EmptyState></Screen>;
  }

  const counts = countByMode(cards);
  const shown = filter === 'all' ? cards : cards.filter((c) => c.modes.includes(filter));
  const hidden = cards.length - shown.length;
  return (
    <Screen
      title={t('shell:tabs.explore')}
      submenuLabel={t('filter.label')}
      // El filtre per tipus és el submenú de la capçalera (§10.4). Només els tipus que tenen cases.
      submenu={[
        { id: 'all', label: t('filter.allShort'), count: cards.length, active: filter === 'all', onSelect: () => setFilter('all') },
        ...MODES.filter((m) => counts[m] > 0).map((m) => ({ id: m, label: t(`modes.${m}`), count: counts[m], active: filter === m, onSelect: () => setFilter(m) })),
      ]}
    >
      {hidden > 0 && <p className="muted small">{t('filter.hidden', { count: hidden })}</p>}
      <ul className="explore-grid">
        {shown.map((c) => {
          const title = pick(c.title, locale, c.sourceLocale).text;
          return (
            <li key={c.propertyId}>
              <Link to={`/p/${c.slug}`} className="explore-card">
                <div className="explore-photo">
                  {c.cover ? <Photo photo={c.cover} sizes="(min-width: 1024px) 40vw, 100vw" locale={locale} source={c.sourceLocale} /> : null}
                </div>
                <h2>{title}</h2>
                {priceFromText(c.priceFrom, c.modes, locale, t) && <p className="explore-price">{priceFromText(c.priceFrom, c.modes, locale, t)}</p>}
                <p className="muted small">{[c.location.municipality, c.location.island].filter(Boolean).join(' · ')} · {c.modes.map((m) => t(`modes.${m}`)).join(', ')}</p>
              </Link>
            </li>
          );
        })}
      </ul>
    </Screen>
  );
}
